/**
 * ============================================================================
 * Demo Emissions Hook
 * ============================================================================
 * 
 * Hook pour gérer les émissions dans la démo Calculator avec:
 * - Persistance localStorage
 * - Calcul des métriques par catégorie
 * - Tracking de la dernière sauvegarde
 * 
 * ============================================================================
 */

import { useEffect, useState } from 'react';

export interface DemoEmission {
  id: string;
  fe_key: string;
  category: string;
  subcategory?: string;
  process_name?: string;
  unit?: string;
  factor_value?: number;
  activity_value: number;
  tco2e: number;
  description?: string;
  created_at: string;
}

export interface CategoryMetrics {
  factorCount: number;
  completedCount: number;
  totalCO2: number;
  benchmark?: number; // Simulated benchmark
}

const STORAGE_KEY = 'calculator_demo_emissions';
const LAST_SAVE_KEY = 'calculator_demo_last_save';

export function useDemoEmissions() {
  const [emissions, setEmissions] = useState<DemoEmission[]>([]);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const lastSave = localStorage.getItem(LAST_SAVE_KEY);
    
    if (stored) {
      try {
        setEmissions(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse stored emissions:', e);
      }
    }
    
    if (lastSave) {
      setLastSaveTime(new Date(lastSave));
    }
  }, []);

  // Save to localStorage whenever emissions change
  const saveEmissions = (newEmissions: DemoEmission[]) => {
    setEmissions(newEmissions);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newEmissions));
    
    const now = new Date();
    setLastSaveTime(now);
    localStorage.setItem(LAST_SAVE_KEY, now.toISOString());
  };

  // Add or update emission
  const addEmission = (emission: Omit<DemoEmission, 'id' | 'created_at' | 'tco2e'>) => {
    const tco2e = (emission.factor_value || 0) * emission.activity_value;
    
    // Check if emission already exists for this FE
    const existingIndex = emissions.findIndex(e => e.fe_key === emission.fe_key);
    
    if (existingIndex >= 0) {
      // Update existing emission
      const updatedEmissions = [...emissions];
      updatedEmissions[existingIndex] = {
        ...updatedEmissions[existingIndex],
        ...emission,
        tco2e,
        created_at: new Date().toISOString(),  // Update timestamp
      };
      saveEmissions(updatedEmissions);
      return updatedEmissions[existingIndex];
    } else {
      // Add new emission
      const newEmission: DemoEmission = {
        ...emission,
        id: `demo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        tco2e,
        created_at: new Date().toISOString(),
      };
      saveEmissions([...emissions, newEmission]);
      return newEmission;
    }
  };

  // Remove emission
  const removeEmission = (id: string) => {
    saveEmissions(emissions.filter(e => e.id !== id));
  };

  // Clear all emissions
  const clearAllEmissions = () => {
    saveEmissions([]);
    localStorage.removeItem(LAST_SAVE_KEY);
    setLastSaveTime(null);
  };

  // Get emissions by category
  const getEmissionsByCategory = (category: string) => {
    return emissions.filter(e => e.category === category);
  };

  // Get emissions by FE key
  const getEmissionsByFactor = (fe_key: string) => {
    return emissions.filter(e => e.fe_key === fe_key);
  };

  // Calculate metrics for a category
  const getCategoryMetrics = (
    category: string,
    totalFactorCount: number
  ): CategoryMetrics => {
    const categoryEmissions = getEmissionsByCategory(category);
    
    // Count unique FE keys (completed factors)
    const uniqueFeKeys = new Set(categoryEmissions.map(e => e.fe_key));
    const completedCount = uniqueFeKeys.size;
    
    // Sum total CO2
    const totalCO2 = categoryEmissions.reduce((sum, e) => sum + e.tco2e, 0);
    
    // Simulate benchmark (random value between 500 and 2000 kg CO2e)
    const benchmark = 500 + Math.random() * 1500;
    
    return {
      factorCount: totalFactorCount,
      completedCount,
      totalCO2,
      benchmark,
    };
  };

  // Get total emissions across all categories
  const getTotalEmissions = () => {
    return emissions.reduce((sum, e) => sum + e.tco2e, 0);
  };

  return {
    emissions,
    lastSaveTime,
    addEmission,
    removeEmission,
    clearAllEmissions,
    getEmissionsByCategory,
    getEmissionsByFactor,
    getCategoryMetrics,
    getTotalEmissions,
  };
}

